export interface AudioQualityMetrics {
  bitrate: number;
  latency: number;
  packetLoss: number;
  jitter: number;
  quality: 'excellent' | 'good' | 'poor' | 'disconnected';
}

export interface ConnectionStats {
  bytesReceived: number;
  bytesSent: number;
  packetsReceived: number;
  packetsSent: number;
  packetsLost: number;
  roundTripTime: number;
}

export class AudioQualityMonitor {
  private statsInterval: NodeJS.Timeout | null = null;
  private peerConnection: RTCPeerConnection | null = null;
  private onQualityChange?: (quality: AudioQualityMetrics) => void;
  private onConnectionStats?: (stats: ConnectionStats) => void;

  constructor(peerConnection: RTCPeerConnection) {
    this.peerConnection = peerConnection;
  }

  startMonitoring(): void {
    if (!this.peerConnection) {
      return;
    }

    // より頻繁に統計情報を取得（接続安定性向上）
    this.statsInterval = setInterval(async () => {
      try {
        const stats = await this.getConnectionStats();
        const quality = this.calculateQuality(stats);
        
        this.onQualityChange?.(quality);
        this.onConnectionStats?.(stats);

        // 接続品質が悪い場合の警告
        if (quality.quality === 'poor' || quality.quality === 'disconnected') {
          console.warn('⚠️ 音声品質が低下しています:', {
            quality: quality.quality,
            packetLoss: quality.packetLoss.toFixed(1) + '%',
            latency: quality.latency.toFixed(0) + 'ms'
          });
        }
      } catch (error) {
        console.error('音声品質監視エラー:', error);
      }
    }, 2000); // 2秒ごとにチェック（負荷軽減）
  }

  stopMonitoring(): void {
    if (this.statsInterval) {
      clearInterval(this.statsInterval);
      this.statsInterval = null;
    }
  }

  private async getConnectionStats(): Promise<ConnectionStats> {
    if (!this.peerConnection) {
      throw new Error('PeerConnectionが存在しません');
    }

    const stats = await this.peerConnection.getStats();
    let bytesReceived = 0;
    let bytesSent = 0;
    let packetsReceived = 0;
    let packetsSent = 0;
    let packetsLost = 0;
    let roundTripTime = 0;

    stats.forEach((report) => {
      if (report.type === 'inbound-rtp' && report.mediaType === 'audio') {
        bytesReceived += report.bytesReceived || 0;
        packetsReceived += report.packetsReceived || 0;
        packetsLost += report.packetsLost || 0;
      } else if (report.type === 'outbound-rtp' && report.mediaType === 'audio') {
        bytesSent += report.bytesSent || 0;
        packetsSent += report.packetsSent || 0;
      } else if (report.type === 'candidate-pair' && report.state === 'succeeded') {
        roundTripTime = report.currentRoundTripTime || 0;
      }
    });

    return {
      bytesReceived,
      bytesSent,
      packetsReceived,
      packetsSent,
      packetsLost,
      roundTripTime: roundTripTime * 1000 // 秒をミリ秒に変換
    };
  }

  private calculateQuality(stats: ConnectionStats): AudioQualityMetrics {
    // パケット損失率を計算
    const totalPackets = stats.packetsReceived + stats.packetsLost;
    const packetLossRate = totalPackets > 0 ? (stats.packetsLost / totalPackets) * 100 : 0;

    // ビットレートを計算（簡易版）
    const bitrate = stats.bytesReceived * 8; // 簡易計算

    // 品質を判定（より厳密な基準）
    let quality: 'excellent' | 'good' | 'poor' | 'disconnected' = 'disconnected';

    if (stats.packetsReceived === 0) {
      quality = 'disconnected';
    } else if (packetLossRate < 0.5 && stats.roundTripTime < 100) {
      quality = 'excellent';
    } else if (packetLossRate < 2 && stats.roundTripTime < 200) {
      quality = 'good';
    } else if (packetLossRate < 5 && stats.roundTripTime < 500) {
      quality = 'poor';
    } else {
      quality = 'disconnected';
    }

    return {
      bitrate,
      latency: stats.roundTripTime,
      packetLoss: packetLossRate,
      jitter: 0, // WebRTC Stats APIから直接取得できないため0に設定
      quality
    };
  }

  setOnQualityChange(callback: (quality: AudioQualityMetrics) => void): void {
    this.onQualityChange = callback;
  }

  setOnConnectionStats(callback: (stats: ConnectionStats) => void): void {
    this.onConnectionStats = callback;
  }

  // ネットワーク状態の監視
  static monitorNetworkQuality(): Promise<'online' | 'offline'> {
    return new Promise((resolve) => {
      if (!navigator.onLine) {
        resolve('offline');
        return;
      }

      // ネットワーク品質の簡易チェック
      const startTime = Date.now();
      fetch('/api/health', { 
        method: 'HEAD',
        cache: 'no-cache'
      })
        .then(() => {
          const latency = Date.now() - startTime;
          resolve(latency < 1000 ? 'online' : 'offline');
        })
        .catch(() => {
          resolve('offline');
        });
    });
  }

  // 音声デバイスの取得
  static async getAudioDevices(): Promise<MediaDeviceInfo[]> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.filter(device => device.kind === 'audioinput');
    } catch (error) {
      console.error('音声デバイス取得エラー:', error);
      return [];
    }
  }

  // マイク権限の確認
  static async checkMicrophonePermission(): Promise<boolean> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      return true;
    } catch (error) {
      console.error('マイク権限確認エラー:', error);
      return false;
    }
  }

  // 音声レベル監視
  static createAudioLevelMonitor(stream: MediaStream, callback: (level: number) => void): () => void {
    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    const microphone = audioContext.createMediaStreamSource(stream);
    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    microphone.connect(analyser);
    analyser.fftSize = 256;

    let isMonitoring = true;

    const monitor = () => {
      if (!isMonitoring) {
        return;
      }

      analyser.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      const level = Math.min(average / 255, 1); // 0-1の範囲に正規化

      callback(level);

      requestAnimationFrame(monitor);
    };

    monitor();

    // 監視停止関数を返す
    return () => {
      isMonitoring = false;
      audioContext.close();
    };
  }

  // 音声品質の推奨設定を取得
  static getRecommendedAudioConstraints(): MediaTrackConstraints {
    return {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      sampleRate: 48000,
      channelCount: 1
    };
  }

  // 接続状態の診断
  static diagnoseConnection(peerConnection: RTCPeerConnection): Promise<{
    iceConnectionState: string;
    connectionState: string;
    iceGatheringState: string;
    signalingState: string;
    recommendations: string[];
  }> {
    return new Promise(async (resolve) => {
      const recommendations: string[] = [];

      // ICE接続状態の確認
      if (peerConnection.iceConnectionState === 'failed') {
        recommendations.push('ICE接続に失敗しました。ネットワーク設定を確認してください。');
      } else if (peerConnection.iceConnectionState === 'disconnected') {
        recommendations.push('接続が切断されました。再接続を試みています...');
      }

      // 接続状態の確認
      if (peerConnection.connectionState === 'failed') {
        recommendations.push('接続に失敗しました。ページをリロードしてください。');
      }

      // シグナリング状態の確認
      if (peerConnection.signalingState === 'have-local-offer') {
        recommendations.push('オファーを送信中です。相手の応答を待っています...');
      } else if (peerConnection.signalingState === 'have-remote-offer') {
        recommendations.push('相手のオファーを受信中です...');
      }

      // 統計情報から追加の診断
      try {
        const stats = await peerConnection.getStats();
        let highPacketLoss = false;
        let highLatency = false;

        stats.forEach((report) => {
          if (report.type === 'inbound-rtp' && report.mediaType === 'audio') {
            const packetLoss = report.packetsLost || 0;
            const packetsReceived = report.packetsReceived || 0;
            const lossRate = packetsReceived > 0 ? (packetLoss / (packetsReceived + packetLoss)) * 100 : 0;
            
            if (lossRate > 5) {
              highPacketLoss = true;
            }
          }
          
          if (report.type === 'candidate-pair' && report.state === 'succeeded') {
            const rtt = report.currentRoundTripTime || 0;
            if (rtt > 0.5) { // 500ms以上
              highLatency = true;
            }
          }
        });

        if (highPacketLoss) {
          recommendations.push('パケット損失が高いです。ネットワーク環境を確認してください。');
        }

        if (highLatency) {
          recommendations.push('遅延が高いです。より良いネットワーク環境に移動してください。');
        }
      } catch (error) {
        console.error('統計情報取得エラー:', error);
      }

      resolve({
        iceConnectionState: peerConnection.iceConnectionState,
        connectionState: peerConnection.connectionState,
        iceGatheringState: peerConnection.iceGatheringState,
        signalingState: peerConnection.signalingState,
        recommendations
      });
    });
  }
}
